/**
 * HomeScreen — Accueil du dialer (W4, B2-home, DES-11).
 *
 * The page starts with its task, not with a grid of shortcuts:
 *   1. « Reprendre la liste <nom> », the primary action (the list selected in
 *      the dialer); with no contact at all, « Importer une liste » instead;
 *   2. « Tâches en retard (n) », the callbacks whose date has passed — the
 *      same query and count as the Tâches tab and the ⌘K palette;
 *   3. « 7 derniers jours » : Appels, Connectés, RDV, Conversion (RDV /
 *      connectés), from the server analytics (W2 metric contracts), each with
 *      its trend against the 7 days before (DES-19) and a daily sparkline.
 * Secondary actions: Importer une liste, Nouveau RDV, Assistant IA. The menu
 * shortcuts and the search bar left: both shells carry them (rail, ⌘K).
 *
 * Data: useDialer() (active customer, lists, caller), useHomeWeekMetrics
 * (POST /api/analytics/kpi), useScheduledCallbacks. Doc:
 * docs/refonte-ui/audit-ui-ux.md « /ParallelDialer/home ».
 */

import { useMemo } from 'react';
import { AlarmClock, ArrowRight, CalendarPlus, Phone, Sparkles, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';
import { formatDate } from '@/i18n/locale';
import { VFButton, VFStatCard } from '@/components/vf';
import { useHomeWeekMetrics } from '@/hooks/useHomeWeekMetrics';
import { useScheduledCallbacks } from '@/hooks/useScheduledCallbacks';
import { countOverdueFollowups } from '@/hooks/useDialerDerivedData';
import { useListCompletion } from '@/hooks/queries/useDialerAnalytics';
import { ENGINE_QUEUE_LIMIT } from '@/hooks/useProspectListBeyondCap';

function fmtDateFull(): string {
  const s = formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function HomeScreen() {
  const { t } = useTranslation(['dialer', 'common']);
  const navigate = useNavigate();
  const {
    callerData,
    prospectsBySource = {},
    prospectQueue,
    selectedSource,
    setSelectedSource,
    selectedCustomerId,
    setImportDialogOpen,
  } = useDialer();

  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const callerName = str(callerData?.full_name) || str(callerData?.caller_name) || str(callerData?.username);
  const firstName = callerName.split(' ')[0];

  // ── 1. The list to resume ─────────────────────────────────────────────────
  const lists = useMemo(
    () => Object.entries(prospectsBySource).filter(([, rows]) => (rows?.length ?? 0) > 0),
    [prospectsBySource],
  );
  const list = lists.find(([name]) => name === selectedSource) ?? lists[0] ?? null;
  // REV-W4-F17: `prospectsBySource` comes from the engine's queue, capped at
  // ENGINE_QUEUE_LIMIT: past it, the count was the loaded part of the list.
  // At the cap, the list's true size comes from the server (list completion);
  // until it answers (or if it fails), « N+ » says the count is a floor.
  const engineCapped = (prospectQueue?.length ?? 0) >= ENGINE_QUEUE_LIMIT;
  const completionQ = useListCompletion(
    { list: list?.[0], ...(selectedCustomerId ? { customer_id: selectedCustomerId } : {}) },
    engineCapped && !!list,
  );
  const serverTotal = completionQ.data?.lists.find((l) => l.list === list?.[0])?.total;
  const listCount = !list ? 0 : !engineCapped ? list[1].length : (serverTotal ?? null);
  const resumeList = () => {
    if (!list) return;
    setSelectedSource(list[0]);
    navigate('/ParallelDialer');
  };
  const openImport = () => setImportDialogOpen(true);

  // ── 2. Overdue tasks (same query as the Tâches tab) ───────────────────────
  const callbacks = useScheduledCallbacks(str(callerData?.client_email) || null, true, selectedCustomerId);
  const overdue = callbacks.isLoading || callbacks.isError ? null : countOverdueFollowups(callbacks.data ?? [], Date.now());
  const goOverdue = () => navigate('/ParallelDialer/followups?tab=tasks');

  // ── 3. Last 7 days, from the server analytics ─────────────────────────────
  const week = useHomeWeekMetrics(selectedCustomerId);
  const tileState = week.isLoading ? 'loading' : week.isError ? 'error' : 'ready';
  const m = week.data;

  const goNewMeeting = () => window.dispatchEvent(new CustomEvent('revcall:new-meeting'));
  const goAIChat = () => navigate('/ParallelDialer/ai-chat');
  const goHistory = () => navigate('/ParallelDialer/call-history');
  const goAnalytics = () => navigate('/ParallelDialer/analytics');
  const goMeetings = () => navigate('/ParallelDialer/followups?tab=agenda');

  return (
    <div className="mx-auto w-full max-w-5xl px-6 pt-3 pb-8">
      <header className="mb-6">
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-[var(--vf-text-1)]">
          {t('dialer:nav.home')}
        </h1>
        <p className="mt-1 text-sm text-[var(--vf-text-3)]">
          {firstName ? `${t('dialer:home.greeting', { name: firstName })} · ` : ''}{fmtDateFull()}
        </p>
      </header>

      {/* 1 + 2 : the task of the day */}
      <section aria-label={t('dialer:home.taskSection')} className="mb-8 grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-vf-lg border border-[var(--vf-border)] bg-[var(--vf-surface-2)] p-5">
          {list ? (
            <>
              <p className="text-[13px] text-[var(--vf-text-3)]">
                {listCount !== null
                  ? t('dialer:home.resume.subtitle', { count: listCount })
                  : t('dialer:home.resume.subtitleAtLeast', { count: list[1].length })}
              </p>
              <VFButton
                variant="primary"
                size="lg"
                className="mt-3 min-h-[44px] max-w-full"
                icon={<Phone size={16} aria-hidden />}
                onClick={resumeList}
              >
                <span className="truncate">{t('dialer:home.resume.cta', { name: list[0] })}</span>
              </VFButton>
            </>
          ) : (
            <>
              <p className="text-[15px] font-semibold text-[var(--vf-text-1)]">{t('dialer:home.resume.emptyTitle')}</p>
              <p className="mt-1 text-[13px] text-[var(--vf-text-3)]">{t('dialer:home.resume.emptyHint')}</p>
              <VFButton
                variant="primary"
                size="lg"
                className="mt-3 min-h-[44px]"
                icon={<Upload size={16} aria-hidden />}
                onClick={openImport}
              >
                {t('dialer:home.quickActions.importList')}
              </VFButton>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={goOverdue}
          className="flex min-h-[44px] items-center gap-3 rounded-vf-lg border border-[var(--vf-border)] bg-[var(--vf-surface-2)] p-5 text-left transition-colors hover:bg-[var(--vf-surface-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)]"
        >
          <AlarmClock
            size={18}
            aria-hidden
            className={overdue ? 'text-[var(--vf-status-failed-fg)]' : 'text-[var(--vf-text-3)]'}
          />
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold text-[var(--vf-text-1)]" data-testid="home-overdue">
              {overdue === null
                ? (callbacks.isError ? t('dialer:home.overdue.error') : t('dialer:home.overdue.loading'))
                : overdue > 0
                  ? t('dialer:home.overdue.label', { count: overdue })
                  : t('dialer:home.overdue.none')}
            </span>
            <span className="block text-[12px] text-[var(--vf-text-3)]">{t('dialer:home.overdue.hint')}</span>
          </span>
          <ArrowRight size={16} aria-hidden className="text-[var(--vf-text-3)]" />
        </button>
      </section>

      {/* 3 : last 7 days */}
      <section aria-labelledby="home-week-title" className="mb-8">
        <h2 id="home-week-title" className="mb-3 text-[15px] font-semibold text-[var(--vf-text-1)]">
          {t('dialer:home.thisWeek')}
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <VFStatCard label={t('dialer:home.stats.calls')} state={tileState} metric={m?.calls} series={m?.series.calls} hint={t('dialer:home.stats.periodHint')} onClick={goHistory} />
          <VFStatCard label={t('dialer:home.stats.connected')} state={tileState} metric={m?.connected} series={m?.series.connected} hint={t('dialer:home.stats.connectedHint')} onClick={goAnalytics} />
          <VFStatCard label={t('dialer:home.stats.meetings')} state={tileState} metric={m?.meetings} series={m?.series.meetings} hint={t('dialer:home.stats.periodHint')} onClick={goMeetings} />
          <VFStatCard label={t('dialer:home.stats.conversion')} state={tileState} metric={m?.conversion} hint={t('dialer:home.stats.conversionHint')} onClick={goAnalytics} />
        </div>
      </section>

      {/* Secondary actions */}
      <div className="flex flex-wrap gap-2">
        <VFButton variant="secondary" className="min-h-[44px]" icon={<Upload size={14} aria-hidden />} onClick={openImport}>
          {t('dialer:home.quickActions.importList')}
        </VFButton>
        <VFButton variant="secondary" className="min-h-[44px]" icon={<CalendarPlus size={14} aria-hidden />} onClick={goNewMeeting}>
          {t('dialer:home.quickActions.newMeeting')}
        </VFButton>
        <VFButton variant="secondary" className="min-h-[44px]" icon={<Sparkles size={14} aria-hidden />} onClick={goAIChat}>
          {t('dialer:home.quickActions.aiAssistant')}
        </VFButton>
      </div>
    </div>
  );
}
